import moment from 'moment';
import type { Moment } from 'moment';

/** Anything moment() can parse: Date, Moment, ISO string, or epoch milliseconds. */
type DateLike = Date | Moment | string | number | null | undefined;

/**
 * Format a date-like value with a moment format string.
 * @param value - Date, Moment, or moment-parseable value.
 * @param format - moment format token string.
 * @returns Formatted string; '' for falsy input.
 */
export function formatDateTime(value: DateLike, format = 'DD.MM.YY HH:mm'): string {
  if (value instanceof Date) {
    return moment(value.toISOString()).format(format);
  } else if (moment.isMoment(value)) {
    return value.format(format);
  }
  if (value) {
    return moment(value).format(format);
  }
  return '';
}

/**
 * A point in time with the month as a short name, in the browser's language,
 * e.g. "5. Okt. 2026, 17:53" or "Oct 5, 2026, 5:53 PM". The way to show dates
 * in the Hub: no numbered months.
 * @returns '' for missing or invalid input
 */
export function formatDateTimeShortMonth(value: Date | string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat(navigator.language, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

/**
 * Like {@link formatDateTimeShortMonth}, but the date only, e.g. "5. Okt. 2026" or "Oct 5, 2026".
 * @returns '' for missing or invalid input
 */
export function formatDateShortMonth(value: Date | string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat(navigator.language, { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
}

/**
 * Format a date-like value in the given locale (mutates a passed Moment's locale).
 * @param value - Moment or moment-parseable value.
 * @param lang - moment locale code, e.g. 'de' or 'en'.
 * @param format - moment format token string.
 * @returns Formatted string; falsy input is passed through as '' or the value itself.
 */
export function i18nDateFormat(value: DateLike, lang = 'de', format = 'DD. MMMM YYYY HH:mm'): string {
  if (moment.isMoment(value)) {
    value.locale(lang);
    return value.format(format);
  } else if (value) {
    const newValue = moment(value);
    newValue.locale(lang);
    return newValue.format(format);
  }

  return (value as any) || '';
}

/**
 * Format only the time part of a date-like value.
 * @returns Formatted string; falsy input is passed through as '' or the value itself.
 */
export function formatTime(value: DateLike, format = 'HH:mm:ss'): string {
  if (moment.isMoment(value)) {
    return value.format(format);
  } else if (value) {
    return moment(value).format(format);
  }
  return (value as any) || '';
}

/**
 * Humanized duration between a future/past point and now, e.g. 'in 3 Tagen'.
 * @param value - Target point in time: Moment/Date/epoch ms (non-string) or a moment-parseable string.
 * @param language - moment locale code used for the humanized output.
 */
export function formatDuration(value: DateLike, language = 'de'): string {
  if (typeof value !== 'string') {
    // `any` casts: original JS relied on implicit Moment→number valueOf() coercion during subtraction
    return moment
      .duration((value as any) - (moment() as any))
      .locale(language)
      .humanize();
  } else if (value) {
    return moment
      .duration((moment(String(value)) as any) - (moment() as any))
      .locale(language)
      .humanize();
  }
  return '';
}


/**
 * Converts a MongoDB ObjectId to a Date object.
 * @param objectId - ObjectId string (or object with a matching toString); its first 8 hex chars are the creation timestamp in seconds.
 */
export function objectIdToDate(objectId: string | { toString(): string }): Date {
  return new Date(parseInt(objectId.toString().substring(0, 8), 16) * 1000);
}
